import React, { useMemo, useState as useLocalState, useEffect as useLocalEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { AnimatePresence, motion } from 'framer-motion';
import { RefreshCw, Plus, Trash2, X, Check, AlertTriangle, Scale, Ghost, Settings } from 'lucide-react';
import { useWeightData, DateRange, fetchProfiles, createProfile, deleteProfile } from '../hooks/useWeightData';
import { Stagger } from '../components/minimal/Stagger';
import { WeightChart, niceCeil, type WeightPoint } from '../components/weight/WeightChart';

const DAY = 86400;

const parseLocalYYYYMMDD = (dateStr: string): Date => {
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return new Date(dateStr);
    return new Date(y, m - 1, d);
};

const toUnix = (dateStr: string): number =>
    Math.floor(parseLocalYYYYMMDD(dateStr).getTime() / 1000);

const RANGE_WINDOWS: { range: DateRange; label: string; secs: number }[] = [
    { range: '7d', label: '7d', secs: 7 * DAY },
    { range: '30d', label: '30d', secs: 30 * DAY },
    { range: '90d', label: '90d', secs: 90 * DAY },
    { range: 'all', label: 'All', secs: 0 },
];

const formatDay = (unix: number, spanSecs: number) => {
    const d = new Date(unix * 1000);
    if (spanSecs > 180 * DAY) {
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    if (spanSecs > 80 * DAY) {
        return d.toLocaleDateString('en-US', { month: 'short' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatStatsDate = (dateStr: string) =>
    parseLocalYYYYMMDD(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function frameTimes(visible: WeightPoint[], rangeSecs: number, nowSec: number) {
    if (!visible.length) {
        const span = rangeSecs || 7 * DAY;
        return { left: nowSec - span, right: nowSec };
    }
    const dataLeft = visible[0].time;
    const dataRight = visible[visible.length - 1].time;
    if (visible.length <= 2) {
        const span = rangeSecs > 0 ? rangeSecs : 7 * DAY;
        const mid = (dataLeft + dataRight) / 2;
        return { left: mid - span / 2, right: mid + span / 2 };
    }
    const span = Math.max(DAY, dataRight - dataLeft);
    const pad = Math.max(span * 0.05, DAY * 0.35);
    return { left: dataLeft - pad, right: dataRight + pad };
}

interface ManageProfilesModalProps {
    profiles: string[];
    activeProfile: string;
    onClose: () => void;
    onProfilesChange: (profiles: string[]) => void;
    onSwitchProfile: (id: string) => void;
}

const ManageProfilesModal: React.FC<ManageProfilesModalProps> = ({
    profiles, activeProfile, onClose, onProfilesChange, onSwitchProfile,
}) => {
    const [newId, setNewId] = useLocalState('');
    const [creating, setCreating] = useLocalState(false);
    const [createError, setCreateError] = useLocalState<string | null>(null);
    const [deletingId, setDeletingId] = useLocalState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useLocalState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useLocalEffect(() => {
        const t = setTimeout(() => inputRef.current?.focus(), 80);
        return () => clearTimeout(t);
    }, []);

    const handleCreate = async () => {
        const trimmed = newId.trim().toLowerCase().replace(/\s+/g, '-');
        if (!trimmed) return;
        if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
            setCreateError('Letters, numbers, hyphens, underscores only.');
            return;
        }
        if (trimmed === 'phantom') {
            setCreateError('"phantom" is reserved.');
            return;
        }
        if (profiles.includes(trimmed)) {
            setCreateError('That profile already exists.');
            return;
        }
        setCreating(true);
        setCreateError(null);
        try {
            await createProfile(trimmed);
            onProfilesChange([...profiles, trimmed]);
            setNewId('');
        } catch (e: unknown) {
            setCreateError(e instanceof Error ? e.message : 'Failed to create profile.');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            await deleteProfile(id);
            const updated = profiles.filter(p => p !== id);
            onProfilesChange(updated);
            if (activeProfile === id) onSwitchProfile('default');
        } catch {
            // keep list on error
        } finally {
            setDeletingId(null);
            setConfirmDeleteId(null);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-end justify-center bg-fg/20 sm:items-center sm:p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full border border-line bg-bg p-6 sm:max-w-sm sm:rounded-lg"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="mb-5 flex items-center justify-between">
                        <h2 className="text-xs font-medium uppercase tracking-widest text-muted">Profiles</h2>
                        <button
                            onClick={onClose}
                            className="p-2 text-faint transition-colors hover:text-fg"
                            aria-label="Close"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    <div className="mb-5 space-y-1">
                        {profiles.length === 0 && (
                            <p className="py-4 text-center text-sm text-faint">No profiles yet.</p>
                        )}
                        {profiles.map(id => (
                            <div
                                key={id}
                                className="flex items-center justify-between py-2.5"
                            >
                                <div className="flex items-center gap-2">
                                    {activeProfile === id && (
                                        <Check size={12} strokeWidth={2.5} className="text-fg" />
                                    )}
                                    <span className="text-sm tracking-tight">{id}</span>
                                    {id === 'default' && (
                                        <span className="text-[10px] uppercase tracking-widest text-faint">default</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    {activeProfile !== id && (
                                        <button
                                            onClick={() => { onSwitchProfile(id); onClose(); }}
                                            className="px-2 py-1 text-xs text-muted transition-colors hover:text-fg"
                                        >
                                            Switch
                                        </button>
                                    )}
                                    {id !== 'default' && (
                                        confirmDeleteId === id ? (
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs text-red-600">Delete?</span>
                                                <button
                                                    onClick={() => handleDelete(id)}
                                                    disabled={deletingId === id}
                                                    className="p-1.5 text-red-600 disabled:opacity-50"
                                                    aria-label="Confirm delete"
                                                >
                                                    <Check size={14} />
                                                </button>
                                                <button
                                                    onClick={() => setConfirmDeleteId(null)}
                                                    className="p-1.5 text-faint hover:text-fg"
                                                    aria-label="Cancel delete"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setConfirmDeleteId(id)}
                                                disabled={deletingId === id}
                                                className="p-1.5 text-faint transition-colors hover:text-red-600 disabled:opacity-50"
                                                aria-label={`Delete ${id}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="border-t border-line pt-4">
                        <p className="mb-3 text-[11px] uppercase tracking-widest text-muted">New profile</p>
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={newId}
                                onChange={e => { setNewId(e.target.value); setCreateError(null); }}
                                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                                placeholder="e.g. sarah"
                                className="flex-1 border border-line bg-bg px-3 py-2 text-sm placeholder:text-faint focus:border-fg focus:outline-none"
                            />
                            <button
                                onClick={handleCreate}
                                disabled={creating || !newId.trim()}
                                className="bg-fg px-3 text-bg disabled:opacity-30"
                                aria-label="Add profile"
                            >
                                {creating ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                            </button>
                        </div>
                        {createError && (
                            <div className="mt-2.5 flex items-center gap-2 text-xs text-red-600">
                                <AlertTriangle size={12} />
                                <span>{createError}</span>
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

const Stat: React.FC<{ label: string; value: string; hint?: string; tone?: 'up' | 'down' | 'flat' }> = ({
    label, value, hint, tone,
}) => (
    <div>
        <div className="mb-1 text-[10px] font-medium uppercase tracking-widest text-muted">{label}</div>
        <div className={`text-[22px] font-medium leading-none tracking-tight tabular-nums ${
            tone === 'down' ? 'text-emerald-700' : tone === 'up' ? 'text-red-600' : 'text-fg'
        }`}>
            {value}
        </div>
        {hint && <div className="mt-1.5 text-[11px] text-faint">{hint}</div>}
    </div>
);

const MinimalWeight: React.FC = () => {
    const {
        weightData,
        loading,
        error,
        lastUpdated,
        refetch,
        isDemoMode,
        activeProfile,
        setActiveProfile,
        settingProfile,
        scaleProfile,
        phantomMode,
        togglePhantomMode,
    } = useWeightData('all');

    const [profiles, setProfiles] = useLocalState<string[]>([]);
    const [loadingProfiles, setLoadingProfiles] = useLocalState(true);
    const [showManage, setShowManage] = useLocalState(false);
    const [hoverKg, setHoverKg] = useLocalState<number | null>(null);
    const [viewRange, setViewRange] = useLocalState<DateRange>('30d');

    useLocalEffect(() => {
        fetchProfiles()
            .then(list => setProfiles(list.filter(p => p !== 'phantom')))
            .catch(() => setProfiles(['default']))
            .finally(() => setLoadingProfiles(false));
    }, []);

    const points: WeightPoint[] = useMemo(
        () =>
            weightData
                .filter(d => Number.isFinite(d.weight))
                .map(d => ({ time: toUnix(d.date), value: d.weight })),
        [weightData],
    );

    const latest = points.length ? points[points.length - 1].value : 0;
    const nowSec = Math.floor(Date.now() / 1000);

    const windowSecs =
        viewRange === 'all'
            ? 0
            : (RANGE_WINDOWS.find(w => w.range === viewRange)?.secs ?? 30 * DAY);

    const cutoff = viewRange === 'all' ? 0 : nowSec - windowSecs;

    const visible = useMemo(
        () => points.filter(p => p.time >= cutoff),
        [points, cutoff],
    );

    const { left: leftTime, right: rightTime } = frameTimes(visible, windowSecs, nowSec);
    const axisSpan = Math.max(DAY, rightTime - leftTime);

    const visibleStats = useMemo(() => {
        if (!visible.length) return null;
        const weights = visible.map(p => p.value);
        const min = Math.min(...weights);
        const max = Math.max(...weights);
        const avg = weights.reduce((s, w) => s + w, 0) / weights.length;
        const last = visible[visible.length - 1].value;
        const change = visible.length > 1 ? last - visible[0].value : 0;
        const dated = weightData.filter(d => toUnix(d.date) >= cutoff);
        const lastPoint = dated[dated.length - 1];
        return {
            min, max, avg, latest: last, change, count: visible.length,
            lastDate: lastPoint?.date ?? '',
        };
    }, [visible, weightData, cutoff]);

    const changeTone = visibleStats
        ? visibleStats.change < -0.05 ? 'down' : visibleStats.change > 0.05 ? 'up' : 'flat'
        : 'flat';

    const yMax = niceCeil((visibleStats?.max ?? latest) || 80);
    const displayKg = hoverKg ?? latest;

    return (
        <div>
            <Helmet>
                <title>Weight | Abhinav Chinnusamy</title>
                <meta name="description" content="Weight from Apple Health, plotted live." />
            </Helmet>

            <Stagger className="flex flex-col">
                <div className="mb-2 flex items-baseline justify-between gap-3">
                    <h1 className="text-base font-medium tracking-tight text-fg">Weight</h1>
                    {!phantomMode && (
                        <button
                            onClick={() => refetch()}
                            disabled={loading}
                            className="text-faint transition-colors hover:text-fg disabled:opacity-40"
                            title="Refresh"
                            aria-label="Refresh"
                        >
                            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                        </button>
                    )}
                </div>
                <p className="mb-8 text-sm text-muted">
                    {phantomMode
                        ? 'Phantom — scale readings are not saved.'
                        : isDemoMode
                            ? 'Demo series.'
                            : 'Apple Health'}
                    {!phantomMode && activeProfile && activeProfile !== 'default' && (
                        <span className="text-faint"> · {activeProfile}</span>
                    )}
                </p>
            </Stagger>

            {showManage && (
                <ManageProfilesModal
                    profiles={profiles}
                    activeProfile={activeProfile}
                    onClose={() => setShowManage(false)}
                    onProfilesChange={setProfiles}
                    onSwitchProfile={setActiveProfile}
                />
            )}

            {phantomMode ? (
                <div className="border-t border-line pt-8">
                    <p className="mb-3 flex items-center gap-2 text-sm text-fg">
                        <Ghost size={14} strokeWidth={1.5} />
                        Nothing is being recorded
                    </p>
                    <p className="max-w-xs text-sm leading-relaxed text-muted">
                        The scale is routing to the phantom profile. History stays untouched until you leave.
                    </p>
                    <button
                        onClick={togglePhantomMode}
                        className="quiet-link mt-6 text-sm text-fg"
                    >
                        Exit phantom
                    </button>
                </div>
            ) : (
                <>
                    {error && (
                        <p className="mb-6 text-sm text-red-600">{error}</p>
                    )}

                    {points.length > 0 && (
                        <div className="stagger-item mb-6" style={{ ['--stagger' as string]: 2 }}>
                            <div className="flex items-baseline gap-2">
                                <span className="text-[40px] font-medium leading-none tracking-tight tabular-nums">
                                    {displayKg.toFixed(1)}
                                </span>
                                <span className="text-sm text-muted">kg</span>
                            </div>
                            {visibleStats && (
                                <p className="mt-2 text-sm text-faint">
                                    {formatStatsDate(visibleStats.lastDate)}
                                    <span className={
                                        changeTone === 'down' ? 'text-emerald-700'
                                        : changeTone === 'up' ? 'text-red-600' : ''
                                    }>
                                        {' '}· {visibleStats.change > 0 ? '+' : visibleStats.change < 0 ? '−' : ''}
                                        {Math.abs(visibleStats.change).toFixed(1)} {viewRange === 'all' ? 'all time' : viewRange}
                                    </span>
                                </p>
                            )}
                        </div>
                    )}

                    <div
                        className="stagger-item mb-2 flex flex-wrap items-center gap-x-1"
                        style={{ ['--stagger' as string]: 3 }}
                    >
                        {RANGE_WINDOWS.map(w => {
                            const active = viewRange === w.range;
                            return (
                                <button
                                    key={w.range}
                                    onClick={() => setViewRange(w.range)}
                                    className={`relative min-h-11 min-w-11 px-2.5 text-xs tracking-wide transition-colors ${
                                        active ? 'text-fg' : 'text-faint hover:text-muted'
                                    }`}
                                >
                                    {w.label}
                                    {active && <span className="absolute inset-x-2 bottom-2 h-px bg-fg" />}
                                </button>
                            );
                        })}
                    </div>

                    <div className="h-[300px] w-full sm:h-[360px]">
                            {visible.length > 0 ? (
                                <WeightChart
                                    points={visible}
                                    yMax={yMax}
                                    leftTime={leftTime}
                                    rightTime={rightTime}
                                    average={visibleStats?.avg}
                                    formatTime={t => formatDay(t, axisSpan)}
                                    onHover={pt => setHoverKg(pt ? pt.value : null)}
                                    seriesKey={`${activeProfile}:${viewRange}`}
                                />
                            ) : (
                                !loading && (
                                    <p className="pt-8 text-sm text-muted">No weigh-ins in this window.</p>
                                )
                            )}
                    </div>

                    {visibleStats && (
                        <div
                            className="stagger-item mt-10 grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4"
                            style={{ ['--stagger' as string]: 5 }}
                        >
                            <Stat
                                label="Latest"
                                value={visibleStats.latest.toFixed(1)}
                                hint={formatStatsDate(visibleStats.lastDate)}
                            />
                            <Stat
                                label="Change"
                                value={`${visibleStats.change > 0 ? '+' : visibleStats.change < 0 ? '−' : ''}${Math.abs(visibleStats.change).toFixed(1)}`}
                                hint="kg"
                                tone={changeTone}
                            />
                            <Stat label="Average" value={visibleStats.avg.toFixed(1)} hint="kg" />
                            <Stat
                                label="Range"
                                value={`${visibleStats.min.toFixed(1)}–${visibleStats.max.toFixed(1)}`}
                                hint="kg"
                            />
                        </div>
                    )}

                    {!loading && points.length === 0 && !error && (
                        <p className="mt-8 text-sm text-muted">No weight data yet.</p>
                    )}

                    <div
                        className="stagger-item mt-12 border-t border-line pt-6"
                        style={{ ['--stagger' as string]: 6 }}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-xs font-medium uppercase tracking-widest text-muted">Profiles</h2>
                            <button
                                onClick={() => setShowManage(true)}
                                className="p-1 text-faint transition-colors hover:text-fg"
                                title="Manage profiles"
                                aria-label="Manage profiles"
                            >
                                <Settings size={14} />
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                            {loadingProfiles ? (
                                <span className="text-faint">…</span>
                            ) : (
                                profiles.map(id => {
                                    const isActive = activeProfile === id;
                                    const isScale = scaleProfile === id;
                                    return (
                                        <button
                                            key={id}
                                            onClick={() => setActiveProfile(id)}
                                            disabled={settingProfile}
                                            className={`transition-colors disabled:opacity-50 ${
                                                isActive ? 'text-fg' : 'text-faint hover:text-muted'
                                            }`}
                                        >
                                            <span className="inline-flex items-center gap-1.5">
                                                {id}
                                                {isScale && <Scale size={11} strokeWidth={1.5} className="text-muted" />}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                            <button
                                onClick={togglePhantomMode}
                                className="text-faint transition-colors hover:text-muted"
                                title="Phantom mode"
                            >
                                phantom
                            </button>
                        </div>
                        {lastUpdated && (
                            <p className="mt-4 text-[11px] text-faint">
                                {lastUpdated}
                                {scaleProfile && (
                                    <span> · next weigh-in → {scaleProfile}</span>
                                )}
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default MinimalWeight;
